import React, {
  PropsWithChildren,
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  DefaultValues,
  FieldPath,
  FieldValues,
  FormProvider,
  MultipleFieldErrors,
  Resolver,
  useForm,
  UseFormReturn,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Schema, z, ZodTypeDef } from 'zod/v3';
import { listenFocusFormInput } from './events/FocusFormInputEvent';
import { ValidationMessagesMap } from '../utils/ValidationMessagesMap';
import { warningMessage } from '../utils';
import {
  AdditionalFormContextProvider,
  FieldArrayRef,
  FormSubmitHandler,
} from './AdditionalFormContext';
import { useGetIdFromName } from './utils';
import { useNavigationBlockers } from '../components';

export type FormRef<FormData extends FieldValues = FieldValues> = {
  form: UseFormReturn<FormData>;
  isBlocked: boolean;
  supressBlock: () => void;
};

export interface FormProps<
  FormData extends FieldValues,
> extends PropsWithChildren {
  schema: Schema<FormData, ZodTypeDef, any>;
  defaultValues?: DefaultValues<FormData>;
  reValidateMode?: 'onChange' | 'onBlur' | 'onSubmit';
  serverErrors?: ValidationMessagesMap;
  onSubmit?: FormSubmitHandler<FormData>;
  formRef?: Ref<FormRef<FormData>>;
  fieldArrayRef?: FieldArrayRef;
  form?: UseFormReturn<FormData>;
  idAttributePrefix?: string;
  blockNavigationOnFirstChange?: boolean;
}

export const Form = <FormData extends FieldValues>(
  props: FormProps<FormData>,
) => {
  const { form, ...rest } = props;
  return form ? (
    <FormInner {...rest} form={form} />
  ) : (
    <CreateForm<FormData> {...props}>
      {(form) => <FormInner {...rest} form={form} />}
    </CreateForm>
  );
};

const CreateForm = <FormData extends FieldValues>(
  props: Omit<FormProps<FormData>, 'children'> & {
    children: (form: UseFormReturn<FormData>) => ReactNode;
  },
) => {
  const {
    schema,
    defaultValues,
    /**
     * reValidateMode: 'onChange' is too expensive for large forms as long as
     * the API for reading errors is not isolated per field:
     * ```
     *  const { errors } = useFormState<FormData>({
     *    name, // does not seem to have any effect but ok, maybe in future versions it will have
     *  });
     *  const fieldErrors: MultipleFieldErrors | undefined = get(errors, name)?.types;
     * ```
     * Maybe it's possible to trigger validation with debounced onChange handler: useForm({ onChange: debounce(() => trigger()) })
     */
    reValidateMode = 'onSubmit',
  } = props;

  const form = useForm<FormData>({
    resolver: typedZodResolver(schema),
    defaultValues,
    criteriaMode: 'all', // report all validation errors not just first one
    reValidateMode,
  });

  return <>{props.children(form)}</>;
};

// Safe wrapper for Zod v3
// TODO: remove this encapsulation once zod has been updated to v4
const typedZodResolver = <T extends z.ZodTypeAny>(
  schema: T,
): Resolver<z.infer<T>> => zodResolver(schema as any);

type FormInnerProps<FormData extends FieldValues> = Omit<
  FormProps<FormData>,
  'form'
> & {
  form: UseFormReturn<FormData>;
};

const FormInner = <FormData extends FieldValues>(
  props: FormInnerProps<FormData>,
) => {
  const {
    schema,
    serverErrors,
    children,
    formRef,
    fieldArrayRef,
    onSubmit = noop,
    form,
    idAttributePrefix = '',
    blockNavigationOnFirstChange = true,
  } = props;

  const { setError, clearErrors } = form;
  const { blockIfNotBlockedYet, isBlocked, supressBlock } =
    useBlockerOnFirstChange(blockNavigationOnFirstChange);

  useEffect(() => {
    if (form.formState.isDirty && blockNavigationOnFirstChange) {
      blockIfNotBlockedYet?.();
    }
  }, [
    blockIfNotBlockedYet,
    blockNavigationOnFirstChange,
    form.formState.isDirty,
  ]);

  useImperativeHandle(
    formRef,
    () => ({
      form,
      isBlocked,
      supressBlock,
    }),
    [form, isBlocked, supressBlock],
  );

  useEffect(() => {
    /**
     * It is imperative to always call clearErrors. Even if there are any server errors.
     * Without this `formState.errors` object itself is not updated (only its contents is)
     * and FormErrors component, which relies on `formState.errors` to be able to list all
     * errors, would not get updated.
     */
    clearErrors();
    if (serverErrors?.size) {
      serverErrors.forEach((validationMessages, name) => {
        setError(name as FieldPath<FormData>, {
          types: validationMessages.reduce<MultipleFieldErrors>(
            (fieldErrors, { type, message }, index) => ({
              ...fieldErrors,
              [`serverError${index}`]:
                type === 'warning' ? warningMessage(message) : message,
            }),
            {},
          ),
        });
      });
    }
  }, [serverErrors, setError, clearErrors]);

  const getIdFromName = useGetIdFromName();
  useEffect(
    () =>
      listenFocusFormInput((event) => {
        const { name, preventScroll } = event.detail;
        const inputElement =
          document.getElementsByName(name)[0] ||
          document.getElementById(getIdFromName(name));

        if (inputElement) {
          inputElement.focus({
            preventScroll: preventScroll ?? false,
            focusVisible: true,
          } as FocusOptions);
        }
      }),
    [],
  );

  useEffect(() => {
    if (!blockNavigationOnFirstChange) {
      supressBlock();
    }
  }, [blockNavigationOnFirstChange, supressBlock]);

  return (
    <FormProvider {...form}>
      <AdditionalFormContextProvider
        schema={schema}
        onSubmit={onSubmit}
        idAttributePrefix={idAttributePrefix}
        fieldArrayRef={fieldArrayRef}
        onChange={() => {
          if (blockNavigationOnFirstChange) {
            blockIfNotBlockedYet?.();
          }
        }}
      >
        {children}
      </AdditionalFormContextProvider>
    </FormProvider>
  );
};

const noop = () => {
  throw new Error(
    'Form submit handler is being consumed (through `useFormSubmitHandler` hook) but no handler was passed to <Form> via `onSubmit` prop',
  );
};

const useBlockerOnFirstChange = (blockNavigationOnFirstChange: boolean) => {
  const [removeBlocker, setRemoveBlocker] = useState<(() => void) | undefined>(
    undefined,
  );
  const { add: addBlocker } = useNavigationBlockers();

  const blockIfNotBlockedYet = useMemo(() => {
    if (removeBlocker) {
      return undefined;
    }
    return () => {
      setRemoveBlocker(() => addBlocker());
    };
  }, [addBlocker, removeBlocker]);

  const supressBlock = useCallback(() => {
    setRemoveBlocker(undefined);
  }, []);

  useEffect(() => removeBlocker, [removeBlocker]);

  useEffect(() => {
    if (!blockNavigationOnFirstChange) {
      supressBlock();
    }
  }, [blockNavigationOnFirstChange, supressBlock]);

  return {
    blockIfNotBlockedYet,
    isBlocked: Boolean(removeBlocker),
    supressBlock,
  };
};
